import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const dashboardCardVariants = cva(
  "overflow-hidden transition-colors",
  {
    variants: {
      variant: {
        default: "border-border",
        success: "border-l-4 border-l-[hsl(var(--status-success))]",
        warning: "border-l-4 border-l-[hsl(var(--status-warning))]",
        error: "border-l-4 border-l-[hsl(var(--status-error))]",
        info: "border-l-4 border-l-[hsl(var(--status-info))]",
        brand: "border-l-4 border-l-[hsl(var(--villavie-blue))]",
        elevated: "shadow-md",
        interactive: "hover:border-primary/20 cursor-pointer",
      },
      size: {
        default: "",
        sm: "text-sm",
        lg: "text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface DashboardCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof dashboardCardVariants> {
  title: string;
  value: string | number;
  subtitle?: string;
  description?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label?: string;
    isPositiveGood?: boolean;
  };
  footer?: React.ReactNode;
  loading?: boolean;
  interactive?: boolean;
  onAction?: () => void;
}

const DashboardCard = React.forwardRef<HTMLDivElement, DashboardCardProps>(
  ({
    className,
    variant,
    size,
    title,
    value,
    subtitle,
    description,
    icon,
    trend,
    footer,
    loading = false,
    interactive = false,
    onAction,
    ...props
  }, ref) => {
    const handleClick = () => {
      if (interactive && onAction) {
        onAction();
      }
    };

    if (loading) {
      return (
        <Card
          ref={ref}
          className={cn(
            dashboardCardVariants({ variant, size }),
            "animate-pulse",
            className
          )}
          {...props}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-6 w-6 bg-muted rounded" />
          </CardHeader>
          <CardContent>
            <div className="h-8 w-32 bg-muted rounded mb-2" />
            <div className="h-3 w-20 bg-muted rounded" />
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        ref={ref}
        className={cn(
          dashboardCardVariants({ variant, size }),
          interactive && "cursor-pointer",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          {icon && (
            <div className="text-muted-foreground">
              {icon}
            </div>
          )}
        </CardHeader>
        
        <CardContent>
          <div className="flex items-baseline justify-between">
            <div className="space-y-1">
              <div className="text-2xl font-bold">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </div>
              {subtitle && (
                <p className="text-xs text-muted-foreground">
                  {subtitle}
                </p>
              )}
            </div>
            
            {trend && (
              <TrendIndicator
                value={trend.value}
                label={trend.label}
                isPositiveGood={trend.isPositiveGood}
              />
            )}
          </div>
          
          {description && (
            <p className="text-xs text-muted-foreground mt-2">
              {description}
            </p>
          )}
          
          {footer && (
            <div className="mt-4">
              {footer}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
);
DashboardCard.displayName = "DashboardCard";

// Trend indicator component
interface TrendIndicatorProps {
  value: number;
  label?: string;
  isPositiveGood?: boolean;
  showIcon?: boolean;
  className?: string;
}

const TrendIndicator = React.forwardRef<HTMLDivElement, TrendIndicatorProps>(
  ({ value, label, isPositiveGood = true, showIcon = true, className }, ref) => {
    const isPositive = value > 0;
    const isNeutral = value === 0;
    const isGood = isNeutral ? true : (isPositiveGood && isPositive) || (!isPositiveGood && !isPositive);
    
    const formatValue = (val: number) => {
      const absValue = Math.abs(val);
      if (label) {
        return `${absValue}${label}`;
      }
      return `${absValue.toFixed(1)}%`;
    };


    const getTrendIcon = () => {
      if (isNeutral) return <Minus className="h-3 w-3" />;
      if (isGood) return <TrendingUp className="h-3 w-3" />;
      return <TrendingDown className="h-3 w-3" />;
    };

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium",
          isGood
            ? "bg-[hsl(var(--status-success))]/10 text-[hsl(var(--status-success))]"
            : isNeutral
            ? "bg-muted text-muted-foreground"
            : "bg-[hsl(var(--status-error))]/10 text-[hsl(var(--status-error))]",
          className
        )}
      >
        {showIcon && getTrendIcon()}
        <span>{formatValue(value)}</span>
      </div>
    );
  }
);
TrendIndicator.displayName = "TrendIndicator";

// Metric card specifically for key performance indicators
const MetricCard = React.forwardRef<HTMLDivElement, DashboardCardProps & {
  prefix?: string;
  suffix?: string;
  precision?: number;
}>(({
  value,
  prefix = "",
  suffix = "",
  precision = 0,
  ...props
}, ref) => {
  const formatValue = (val: string | number) => {
    if (typeof val === 'string') return `${prefix}${val}${suffix}`;
    return `${prefix}${val.toFixed(precision)}${suffix}`;
  };

  return (
    <DashboardCard
      ref={ref}
      value={formatValue(value)}
      variant="elevated"
      {...props}
    />
  );
});
MetricCard.displayName = "MetricCard";

// Quick action card for dashboard shortcuts
const QuickActionCard = React.forwardRef<HTMLDivElement, {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action: React.ReactNode;
  className?: string;
}>(({ title, description, icon, action, className, ...props }, ref) => (
  <Card
    ref={ref}
    className={cn(className)}
    {...props}
  >
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          {icon && (
            <div className="text-primary">
              {icon}
            </div>
          )}
          <div>
            <h3 className="font-semibold">{title}</h3>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        <div className="ml-4">
          {action}
        </div>
      </div>
    </CardContent>
  </Card>
));
QuickActionCard.displayName = "QuickActionCard";

// Status card for system health or alerts
const StatusCard = React.forwardRef<HTMLDivElement, {
  title: string;
  status: 'success' | 'warning' | 'error' | 'info';
  message: string;
  action?: React.ReactNode;
  className?: string;
}>(({ title, status, message, action, className, ...props }, ref) => {
  const statusConfig = {
    success: {
      variant: 'success' as const,
      icon: '✓',
      bgColor: 'bg-[hsl(var(--status-success))]/10',
      textColor: 'text-[hsl(var(--status-success))]',
    },
    warning: {
      variant: 'warning' as const,
      icon: '⚠',
      bgColor: 'bg-[hsl(var(--status-warning))]/10',
      textColor: 'text-[hsl(var(--status-warning))]',
    },
    error: {
      variant: 'error' as const,
      icon: '✕',
      bgColor: 'bg-[hsl(var(--status-error))]/10',
      textColor: 'text-[hsl(var(--status-error))]',
    },
    info: {
      variant: 'info' as const,
      icon: 'ℹ',
      bgColor: 'bg-[hsl(var(--status-info))]/10',
      textColor: 'text-[hsl(var(--status-info))]',
    },
  };

  const config = statusConfig[status];

  return (
    <Card
      ref={ref}
      className={cn(
        dashboardCardVariants({ variant: config.variant }),
        "hidden md:block", // Hide on mobile
        className
      )}
      {...props}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
              config.bgColor
            )}>
              <span className={cn("text-sm font-medium", config.textColor)}>
                {config.icon}
              </span>
            </div>
            <div className="space-y-0">
              <h4 className="font-medium text-sm">{title}</h4>
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          </div>
          {action && (
            <div className="flex-shrink-0">
              {action}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});
StatusCard.displayName = "StatusCard";

export {
  DashboardCard,
  TrendIndicator,
  MetricCard,
  QuickActionCard,
  StatusCard,
  dashboardCardVariants,
};
